// ============================================================
// MongoDBIntro.jsx
//
// A branded, educational content page comparing Relational (SQL)
// and Document-oriented (NoSQL) database paradigms.
//
// Brand palette:
//   Background  → #001E2B  (MongoDB Deep Navy)
//   Primary     → #00ED64  (MongoDB Spring Green)
//   Secondary   → #00684A  (Dark Green / Professional)
//   Text        → #FFFFFF  (White)
//   Surface     → rgba(255,255,255,0.03)
// ============================================================

import React, { useState } from "react";
import {
    Box,
    Container,
    Typography,
    Grid,
    Chip,
    Divider,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Avatar,
} from "@mui/material";

/* MUI Icons */
import StorageIcon from "@mui/icons-material/Storage";
import SchemaIcon from "@mui/icons-material/Schema";
import SpeedIcon from "@mui/icons-material/Speed";
import HeightIcon from "@mui/icons-material/Height";
import SchoolIcon from "@mui/icons-material/School";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import FactCheckIcon from "@mui/icons-material/FactCheck";

/* ── Brand tokens ────────────────────────────────────────── */
const BRAND = {
    bg: "#001E2B",
    surface: "rgba(255,255,255,0.04)",
    border: "rgba(0, 237, 100, 0.2)",
    borderHover: "rgba(0, 237, 100, 0.6)",
    green: "#00ED64",
    darkGreen: "#00684A",
    white: "#FFFFFF",
    muted: "rgba(255,255,255,0.6)",
};

/* ── Sub-Components ──────────────────────────────────────── */

function ComparisonCard({ title, icon, sqlText, noSqlText }) {
    return (
        <Paper elevation={0} sx={{
            p: 3, background: BRAND.surface, border: `1px solid ${BRAND.border}`,
            borderRadius: 4, transition: "0.3s", height: "100%",
            "&:hover": { borderColor: BRAND.green, transform: "translateY(-4px)" }
        }}>
            <Stack spacing={2}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Avatar sx={{ bgcolor: `${BRAND.green}15`, color: BRAND.green }}>{icon}</Avatar>
                    <Typography variant="h6" sx={{ color: BRAND.white, fontWeight: 700 }}>{title}</Typography>
                </Box>
                <Box>
                    <Typography variant="caption" sx={{ color: BRAND.green, fontWeight: 800, textTransform: "uppercase" }}>SQL (Relational)</Typography>
                    <Typography variant="body2" sx={{ color: BRAND.muted, mb: 2 }}>{sqlText}</Typography>

                    <Typography variant="caption" sx={{ color: "#00E0FF", fontWeight: 800, textTransform: "uppercase" }}>NoSQL (Document)</Typography>
                    <Typography variant="body2" sx={{ color: BRAND.muted }}>{noSqlText}</Typography>
                </Box>
            </Stack>
        </Paper>
    );
}

/* ── Main Component ──────────────────────────────────────── */

export default function MongoDBIntro() {
    return (
        <Box sx={{ minHeight: "100vh", background: BRAND.bg, py: { xs: 6, md: 10 }, color: BRAND.white }}>
            <Container maxWidth="lg">

                {/* 1. HERO HEADER */}
                <Box sx={{ mb: 10, textAlign: { xs: "center", md: "left" } }}>
                    <Chip
                        icon={<SchoolIcon style={{ color: BRAND.green }} />}
                        label="NoSQL Foundations · Module 01"
                        sx={{ bgcolor: "rgba(0,237,100,0.1)", color: BRAND.green, border: `1px solid ${BRAND.border}`, mb: 2 }}
                    />
                    <Typography variant="h2" sx={{ fontWeight: 900, mb: 2, letterSpacing: "-1.5px" }}>
                        SQL vs. <span style={{ color: BRAND.green }}>NoSQL</span>
                    </Typography>
                    <Typography sx={{ color: BRAND.muted, fontSize: 18, maxWidth: 800, lineHeight: 1.7 }}>
                        The database landscape has evolved. While SQL relies on rigid tables and joins,
                        NoSQL offers a flexible, document-based approach designed for massive scale and rapid development.
                    </Typography>
                </Box>

                {/* 2. THE FUNDAMENTAL SHIFT */}
                <Box sx={{ mb: 10 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 4 }}>
                        <SwapHorizIcon size={32} color={BRAND.green} />
                        <Typography variant="h5" sx={{ fontWeight: 800 }}>The Paradigm Shift</Typography>
                    </Box>



                    <Grid container spacing={3} sx={{ mt: 2 }}>
                        <Grid item xs={12} md={4}>
                            <ComparisonCard
                                title="Data Structure" icon={<SchemaIcon />}
                                sqlText="Tabular: Data is stored in rows and columns with fixed schemas."
                                noSqlText="Document: Data is stored as JSON-like documents (BSON) with dynamic schemas."
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <ComparisonCard
                                title="Scaling" icon={<HeightIcon />}
                                sqlText="Vertical: Scale by adding more power (CPU/RAM) to a single server."
                                noSqlText="Horizontal: Scale by adding more servers (Sharding) to a cluster."
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <ComparisonCard
                                title="Relationships" icon={<StorageIcon />}
                                sqlText="Joins: Relies on complex JOIN statements to link multiple tables."
                                noSqlText="Embedding: Data is often nested inside a single document for speed."
                            />
                        </Grid>
                    </Grid>
                </Box>

                <Divider sx={{ borderColor: "rgba(255,255,255,0.05)", mb: 10 }} />

                {/* 3. CORE COMPARISON MATRIX */}
                <Box sx={{ mb: 10 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 4 }}>
                        <FactCheckIcon sx={{ color: BRAND.green }} />
                        <Typography variant="h5" sx={{ fontWeight: 800 }}>Feature Comparison Matrix</Typography>
                    </Box>



                    [Image of CAP Theorem diagram consistency availability partition tolerance]


                    <TableContainer component={Paper} sx={{ bgcolor: BRAND.surface, border: `1px solid ${BRAND.border}`, borderRadius: 4, mt: 4 }}>
                        <Table>
                            <TableHead sx={{ bgcolor: "rgba(255,255,255,0.05)" }}>
                                <TableRow>
                                    <TableCell sx={{ color: BRAND.green, fontWeight: 700 }}>Feature</TableCell>
                                    <TableCell sx={{ color: BRAND.white }}>Relational (SQL)</TableCell>
                                    <TableCell sx={{ color: BRAND.white }}>NoSQL (Document)</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {[
                                    { f: "Primary Goal", sql: "Data Integrity & Normalization", nosql: "Performance & Scalability" },
                                    { f: "Schema", sql: "Pre-defined (Rigid)", nosql: "Dynamic (Flexible)" },
                                    { f: "Complex Queries", sql: "Excellent (SQL Joins)", nosql: "Good (Aggregation Framework)" },
                                    { f: "ACID Compliance", sql: "Full support by default", nosql: "Support for multi-doc transactions" },
                                    { f: "Use Case", sql: "ERP, Finance, Complex Admin", nosql: "Big Data, Real-time Apps, CMS" }
                                ].map((row, i) => (
                                    <TableRow key={i}>
                                        <TableCell sx={{ color: BRAND.white, fontWeight: 700 }}>{row.f}</TableCell>
                                        <TableCell sx={{ color: BRAND.muted }}>{row.sql}</TableCell>
                                        <TableCell sx={{ color: BRAND.muted }}>{row.nosql}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>

                {/* 4. WHY CHOOSE MONGODB? */}
                <Box sx={{ mb: 10 }}>
                    <Typography variant="h5" sx={{ fontWeight: 800, mb: 4, textAlign: "center" }}>Why Modern Teams Choose MongoDB</Typography>
                    <Grid container spacing={4}>
                        <Grid item xs={12} md={6}>
                            <Box sx={{ p: 4, borderRadius: 4, bgcolor: "rgba(0, 237, 100, 0.05)", border: `1px solid ${BRAND.border}` }}>
                                <Typography variant="h6" sx={{ color: BRAND.green, mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
                                    <LightbulbIcon /> Agile Development
                                </Typography>
                                <Typography sx={{ color: BRAND.muted, fontSize: 15, lineHeight: 1.8 }}>
                                    In a fast-moving startup, requirements change daily. With MongoDB, you can add new fields
                                    to documents without running expensive <code>ALTER TABLE</code> migrations. The database
                                    adapts to your code, not the other way around.
                                </Typography>
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Box sx={{ p: 4, borderRadius: 4, bgcolor: "rgba(0, 224, 255, 0.05)", border: "1px solid rgba(0, 224, 255, 0.2)" }}>
                                <Typography variant="h6" sx={{ color: "#00E0FF", mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
                                    <SpeedIcon /> High Performance
                                </Typography>
                                <Typography sx={{ color: BRAND.muted, fontSize: 15, lineHeight: 1.8 }}>
                                    Because related data is stored together in a single document, MongoDB requires fewer
                                    disk lookups than SQL. This results in significantly faster read performance for
                                    applications with high traffic.
                                </Typography>
                            </Box>
                        </Grid>
                    </Grid>
                </Box>

                {/* FOOTER CTA */}
                <Box sx={{
                    p: 8, borderRadius: 8, textAlign: "center",
                    background: `linear-gradient(45deg, ${BRAND.bg}, #00684A)`,
                    border: `1px solid ${BRAND.border}`,
                    boxShadow: `0 20px 40px rgba(0,0,0,0.5)`
                }}>
                    <StorageIcon style={{ fontSize: 40, color: BRAND.green, marginBottom: 16 }} />
                    <Typography variant="h5" sx={{ fontWeight: 800, mb: 1 }}>Ready to Think in Documents?</Typography>
                    <Typography sx={{ color: BRAND.muted, mb: 4 }}>Unlearn the table-first mindset and embrace the power of flexible data.</Typography>
                    <Stack direction="row" spacing={2} justifyContent="center">
                        <Chip label="Explore BSON Model" onClick={() => {}} sx={{ bgcolor: BRAND.green, color: BRAND.bg, fontWeight: 800, px: 3 }} />
                        <Chip label="Install MongoDB Shell" variant="outlined" onClick={() => {}} sx={{ borderColor: BRAND.muted, color: BRAND.white }} />
                    </Stack>
                </Box>

            </Container>
        </Box>
    );
}
